"use client"

import { useRef, useState } from "react"
import { Autocomplete } from "@react-google-maps/api"
import { Controller, useFormContext, Control, FieldValues, ControllerRenderProps } from "react-hook-form"
import FormTextInputWrapper from "@/shared/components/form-inputs/form-input-wrapper"
import { Input } from "@/shared/components/ui/new-input"

function toLatLngLiteral(
  loc: google.maps.LatLng | google.maps.LatLngLiteral
): google.maps.LatLngLiteral {
  if (typeof (loc as google.maps.LatLng).lat === "function") {
    const ll = loc as google.maps.LatLng
    return { lat: ll.lat(), lng: ll.lng() }
  }
  return loc as google.maps.LatLngLiteral
}

interface PartnerAddressAutocompleteProps {
  control: Control<any>
  disabled?: boolean
}

export const PartnerAddressAutocomplete: React.FC<PartnerAddressAutocompleteProps> = ({ 
  control, 
  disabled 
}) => {
  const { setValue } = useFormContext()
  const [error, setError] = useState<string>("")
  const acRef = useRef<google.maps.places.Autocomplete | null>(null)

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    acRef.current = autocomplete
  }

  const handlePlaceChanged = (onChange: (val: string) => void) => {
    if (!acRef.current) return
    const place = acRef.current.getPlace()
    if (!place.formatted_address) return
    onChange(place.formatted_address)

    const components = place.address_components
    if (!place || !components || !place.geometry?.location) return

    let city = ""
    let stateShortName = ""
    let countryShortName = ""
    let zipCode = ""

    components.forEach((component) => {
      const types = component.types
      if (types.includes("locality")) city = component.long_name
      if (types.includes("administrative_area_level_1"))
        stateShortName = component.long_name
      if (types.includes("country")) countryShortName = component.long_name
      if (types.includes("postal_code")) zipCode = component.long_name
    })

    const { lat, lng } = toLatLngLiteral(place.geometry.location)

    if (!stateShortName || !countryShortName) {
      setError("Please select a valid address that includes both state and country.")
      setValue("city", "", { shouldDirty: true })
      setValue("state", "", { shouldDirty: true })
      setValue("zipCode", "", { shouldDirty: true })
      return
    }

    setError("")
    setValue("city", city, { shouldDirty: true })
    setValue("state", stateShortName, { shouldDirty: true })
    setValue("zipCode", zipCode, { shouldDirty: true })
  }

  return (
    <div className="w-full">
      <Controller
        name="address"
        control={control}
        render={({ field }: { field: ControllerRenderProps<FieldValues, any> }) => {
          return (
            <Autocomplete
              onLoad={handleLoad}
              onPlaceChanged={() => handlePlaceChanged(field.onChange)}
              options={{ types: ["address"] }}
            >
              <FormTextInputWrapper
                inputGroupProps={{
                  label: "Address",
                }}
              >
                <Input
                  placeholder="Street address"
                  value={field.value || ""}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    setError("")
                  }}
                  disabled={disabled}
                />
              </FormTextInputWrapper>
            </Autocomplete>
          )
        }}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  )
}

